<template>
  <div class="quota-allocation-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-plus"></i>
          <span>{{ editingId ? $t('resourceManagement.editQuota') : $t('resourceManagement.allocateQuota') }}</span>
        </div>
        <div class="header-actions">
          <el-button size="small" icon="el-icon-back" @click="$router.back()">{{ $t('common.back') }}</el-button>
        </div>
      </div>
    </div>

    <div class="allocation-main-content">
      <el-card shadow="hover">
        <el-form
          ref="quotaForm"
          :model="quotaForm"
          :rules="quotaRules"
          label-width="120px"
          class="quota-form"
        >
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item :label="$t('common.user')" prop="userId">
                <el-select v-model="quotaForm.userId" :placeholder="$t('common.selectPlaceholder')" filterable :disabled="!!editingId">
                  <el-option
                    v-for="user in userList"
                    :key="user.id"
                    :label="user.username"
                    :value="user.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('resourceManagement.quotaValue')" prop="allocatedTokens">
                <el-input
                  v-model.number="quotaForm.allocatedTokens"
                  :placeholder="$t('resourceManagement.quotaValuePlaceholder')"
                  type="number"
                  min="0"
                >
                  <template slot="append">Token</template>
                </el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('resourceManagement.effectiveTime')" prop="effectiveTime">
                <el-date-picker
                  v-model="quotaForm.effectiveTime"
                  type="datetime"
                  :placeholder="$t('resourceManagement.effectiveTimePlaceholder')"
                  style="width: 100%"
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('resourceManagement.expiryTime')" prop="expiryTime">
                <el-date-picker
                  v-model="quotaForm.expiryTime"
                  type="datetime"
                  :placeholder="$t('resourceManagement.expiryTimePlaceholder')"
                  style="width: 100%"
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item :label="$t('resourceManagement.threshold')" prop="threshold">
                <el-slider
                  v-model="quotaForm.threshold"
                  :min="50"
                  :max="95"
                  show-input
                  :format-tooltip="formatTooltip"
                ></el-slider>
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('common.description')">
                <el-input
                  v-model="quotaForm.remark"
                  :placeholder="$t('common.placeholder')"
                  type="textarea"
                  :rows="3"
                  maxlength="200"
                  show-word-limit
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item>
            <el-button type="primary" @click="handleAllocateQuota" :loading="submitting">
              {{ $t('common.submit') }}
            </el-button>
            <el-button @click="handleFormReset">
              {{ $t('common.reset') }}
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
  </div>
</template>

<script>

export default {
  name: 'QuotaAllocation',
  data() {
    return {
      editingId: null,
      submitting: false,
      userList: [],
      quotaForm: {
        userId: '',
        allocatedTokens: 0,
        effectiveTime: '',
        expiryTime: '',
        threshold: 80,
        remark: ''
      },
      quotaRules: {
        userId: [
          { required: true, message: this.$t('common.selectPlaceholder'), trigger: 'change' }
        ],
        allocatedTokens: [
          { required: true, message: this.$t('resourceManagement.quotaValuePlaceholder'), trigger: 'blur' },
          { type: 'number', min: 1, message: this.$t('resourceManagement.quotaValueMin'), trigger: 'blur' }
        ],
        effectiveTime: [
          { required: true, message: this.$t('resourceManagement.effectiveTimePlaceholder'), trigger: 'change' }
        ],
        remark: [
          { required: false, trigger: 'blur' },
          { max: 200, message: this.$t('common.remarkLength'), trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.editingId = this.$route.query.id
    this.fetchUserList()
    if (this.editingId) {
      this.fetchQuotaDetail()
    }
  },
  methods: {
    fetchUserList() {
      this.$store.dispatch('user/fetchUsers', { page: 1, pageSize: 1000 }).then(response => {
        if (response && response.list) {
          this.userList = response.list
        } else if (Array.isArray(response)) {
          this.userList = response
        }
      }).catch(error => {
        this.$message.error(this.$t('common.loadFailed') + '：' + error.message)
      })
    },
    fetchQuotaDetail() {
      this.$store.dispatch('resource/fetchResourceQuotaDetail', this.editingId).then(response => {
        if (response) {
          // fetchResourceQuotaDetail 返回的是单个对象，可能直接是 data 或者包含在 data 字段中
          const quota = response.data || response
          this.quotaForm = {
            userId: quota.userId,
            allocatedTokens: quota.allocatedTokens,
            effectiveTime: quota.effectiveTime,
            expiryTime: quota.expiryTime,
            threshold: quota.threshold || 80,
            remark: quota.remark
          }
        }
      }).catch(error => {
        this.$message.error(this.$t('common.loadFailed') + '：' + error.message)
      })
    },
    handleAllocateQuota() {
      this.$refs.quotaForm.validate(valid => {
        if (valid) {
          this.submitting = true
          const action = this.editingId 
            ? this.$store.dispatch('resource/updateResourceQuota', { id: this.editingId, quotaData: this.quotaForm })
            : this.$store.dispatch('resource/allocateResourceQuota', this.quotaForm)
          
          action.then(() => {
            this.$message.success(this.editingId ? this.$t('resourceManagement.quotaUpdateSuccess') : this.$t('resourceManagement.quotaAllocateSuccess'))
            this.$router.push('/resource/quota')
          }).catch(error => {
            this.$message.error(this.editingId ? this.$t('resourceManagement.quotaUpdateFailed') : this.$t('resourceManagement.quotaAllocateFailed'))
          }).finally(() => {
            this.submitting = false
          })
        }
      })
    },
    handleFormReset() {
      if (this.editingId) {
        this.fetchQuotaDetail()
      } else {
        this.quotaForm = {
          userId: '',
          allocatedTokens: 0,
          effectiveTime: '',
          expiryTime: '',
          threshold: 80,
          remark: ''
        }
      }
      this.$nextTick(() => {
        this.$refs.quotaForm && this.$refs.quotaForm.clearValidate()
      })
    },
    formatTooltip(val) {
      return val + '%'
    }
  }
}
</script>

<style scoped lang="scss">
.quota-allocation-container {
  margin: -20px;
  padding: 0;
  height: calc(100% + 40px);
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;

  .control-panel {
    background-color: #fff;
    padding: 10px 20px;
    border-bottom: 1px solid #e6e6e6;
    flex-shrink: 0;

    .control-container {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-info {
        display: flex;
        align-items: center;
        font-size: 16px;
        font-weight: bold;
        color: #303133;

        i {
          margin-right: 8px;
          color: #409eff;
        }
      }
    }
  }

  .allocation-main-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
  }
}
</style>
